import React, { useState, useEffect, useRef } from "react";
import ServicesBackground from "./ServicesBackground";

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: "-50px"
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className="min-h-screen px-4 md:px-16 py-32 bg-black/20 relative"
    >
      <div className="hidden md:block">
        <ServicesBackground />
      </div>

      <h3 
        className={`text-3xl font-mono text-center mb-4 transform transition-all duration-700 ${
          isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
        }`}
      >
        &gt; My Services
      </h3>

      <p 
        className={`text-gray-400 text-center max-w-3xl mx-auto mb-32 font-mono text-sm transform transition-all duration-700 delay-200 ${
          isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        I help individuals and teams bring  ideas to life with clean, responsive, and scalable
       development. Here's what I can offer:
      </p>

      <div className="relative max-w-5xl mx-auto">
        {/* First Card - Left */}
        <div 
          className={`group relative bg-black/20 backdrop-blur-sm border-[3px] border-white/20 rounded-2xl p-8 mb-32 transform transition-all duration-700 delay-300 w-full max-w-2xl shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:border-[#FFD700]/50 
          before:content-[''] before:absolute before:left-[-30px] before:top-[30px] before:w-[2px] before:h-[calc(100%-30px)] before:bg-transparent hover:before:bg-[#FFD700]/30 before:transition-colors before:duration-300
          after:content-[''] after:absolute after:left-[30px] after:bottom-[-30px] after:w-[calc(100%-60px)] after:h-[2px] after:bg-transparent hover:after:bg-[#FFD700]/30 after:transition-colors after:duration-300
          [&>div:last-child]:content-[''] [&>div:last-child]:absolute [&>div:last-child]:left-[-30px] [&>div:last-child]:bottom-[-30px] [&>div:last-child]:w-[60px] [&>div:last-child]:h-[60px] [&>div:last-child]:border-l-[2px] [&>div:last-child]:border-b-[2px] [&>div:last-child]:border-transparent hover:[&>div:last-child]:border-[#FFD700]/30 [&>div:last-child]:rounded-bl-3xl [&>div:last-child]:transition-colors [&>div:last-child]:duration-300 ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="text-2xl text-white bg-white/5 p-2 rounded-lg border border-white/20 transition-all duration-300 group-hover:text-[#FFD700] group-hover:border-[#FFD700]/30 group-hover:drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]">
              &lt;/&gt;
            </div>
            <div>
              <h4 className="text-xl font-mono mb-3">Web App Development</h4>
              <p className="text-gray-400 font-mono leading-relaxed">
              I work with a variety of languages and frameworks, like HTML, CSS, JavaScript, TypeScript, React, Node JS, etc to build web applications that are secure, scalable, and fast.
               From simple websites to full-featured web apps, I focus on writing clean, maintainable code and creating smooth, reliable experiences for users.
              </p>
            </div>
          </div>
          <div className="corner-border"></div>
        </div>

        {/* First horizontal line - positioned in middle of space */}
        <div className="absolute left-[calc(25%+2rem)] right-[calc(25%+2rem)] top-[33%]">
          <div className="w-full border-t-2 border-dashed border-[#1e90ff]/30"></div>
        </div>

        {/* Upper vertical line from first horizontal line */}
        <div className="absolute right-[calc(25%+2rem-1px)] top-[16.5%] h-[16.5%] w-0 border-l-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Horizontal line connecting to first card */}
        <div className="absolute right-[calc(25%+2rem)] top-[16.5%] w-[6%] border-t-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Vertical connecting line */}
        <div className="absolute left-[calc(25%+2rem-1px)] top-[33%] h-[34%] w-0 border-l-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Middle horizontal connecting line to second card */}
        <div className="absolute left-[calc(25%+2rem)] top-[50%] w-[6%] border-t-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Second Card - Right */}
        <div 
          className={`group relative bg-black/20 backdrop-blur-sm border-[3px] border-white/20 rounded-2xl p-8 ml-auto mb-32 -mt-8 transform transition-all duration-700 delay-500 w-full max-w-2xl shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:border-[#FFD700]/50 
          before:content-[''] before:absolute before:right-[-30px] before:top-[30px] before:w-[2px] before:h-[calc(100%-30px)] before:bg-transparent hover:before:bg-[#FFD700]/30 before:transition-colors before:duration-300
          after:content-[''] after:absolute after:left-[30px] after:bottom-[-30px] after:w-[calc(100%-60px)] after:h-[2px] after:bg-transparent hover:after:bg-[#FFD700]/30 after:transition-colors after:duration-300
          [&>div:last-child]:content-[''] [&>div:last-child]:absolute [&>div:last-child]:right-[-30px] [&>div:last-child]:bottom-[-30px] [&>div:last-child]:w-[60px] [&>div:last-child]:h-[60px] [&>div:last-child]:border-r-[2px] [&>div:last-child]:border-b-[2px] [&>div:last-child]:border-transparent hover:[&>div:last-child]:border-[#FFD700]/30 [&>div:last-child]:rounded-br-3xl [&>div:last-child]:transition-colors [&>div:last-child]:duration-300 ${
            isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="text-2xl text-white bg-white/5 p-2 rounded-lg border border-white/20 transition-all duration-300 group-hover:text-[#FFD700] group-hover:border-[#FFD700]/30 group-hover:drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]">
              🖥️
            </div>
            <div>
              <h4 className="text-xl font-mono mb-3">Code Optimization</h4>
              <p className="text-gray-400 font-mono leading-relaxed">
                I help improve existing codebases by cleaning up legacy code, applying best practices,
                ensuring responsiveness across devices, and compatibility with modern frameworks.
              </p>
            </div>
          </div>
          <div className="corner-border"></div>
        </div>

        {/* Second horizontal line - positioned in middle of space */}
        <div className="absolute left-[calc(25%+2rem)] right-[calc(25%+2rem)] top-[67%]">
          <div className="w-full border-t-2 border-dashed border-[#1e90ff]/30"></div>
        </div>

        {/* Lower vertical line from second horizontal line */}
        <div className="absolute right-[calc(25%+2rem-1px)] top-[67%] h-[16.5%] w-0 border-l-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Horizontal line connecting to third card */}
        <div className="absolute right-[calc(25%+2rem)] top-[83.5%] w-[6%] border-t-2 border-dashed border-[#1e90ff]/30"></div>

        {/* Third Card - Left */}
        <div 
          className={`group relative bg-black/20 backdrop-blur-sm border-[3px] border-white/20 rounded-2xl p-8 transform transition-all duration-700 delay-700 w-full max-w-2xl shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:border-[#FFD700]/50 
          before:content-[''] before:absolute before:left-[-30px] before:top-[30px] before:w-[2px] before:h-[calc(100%-30px)] before:bg-transparent hover:before:bg-[#FFD700]/30 before:transition-colors before:duration-300
          after:content-[''] after:absolute after:left-[30px] after:bottom-[-30px] after:w-[calc(100%-60px)] after:h-[2px] after:bg-transparent hover:after:bg-[#FFD700]/30 after:transition-colors after:duration-300
          [&>div:last-child]:content-[''] [&>div:last-child]:absolute [&>div:last-child]:left-[-30px] [&>div:last-child]:bottom-[-30px] [&>div:last-child]:w-[60px] [&>div:last-child]:h-[60px] [&>div:last-child]:border-l-[2px] [&>div:last-child]:border-b-[2px] [&>div:last-child]:border-transparent hover:[&>div:last-child]:border-[#FFD700]/30 [&>div:last-child]:rounded-bl-3xl [&>div:last-child]:transition-colors [&>div:last-child]:duration-300 ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="text-2xl text-white bg-white/5 p-2 rounded-lg border border-white/20 transition-all duration-300 group-hover:text-[#FFD700] group-hover:border-[#FFD700]/30 group-hover:drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]">
              👥
            </div>
            <div>
              <h4 className="text-xl font-mono mb-3">Collaboration & Handoff</h4>
              <p className="text-gray-400 font-mono leading-relaxed">
                I collaborate closely with designers, other developers, and project managers to
                ensure smooth handoffs, effective communication, and project alignment from start to
                finish.
              </p>
            </div>
          </div>
          <div className="corner-border"></div>
        </div>
      </div>
    </section>
  );
}